import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";

const MemberAuthContext = createContext(null);
const TOKEN_KEY = "gmoaa_member_token";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const getMemberToken = () => localStorage.getItem(TOKEN_KEY);

export const MemberAuthProvider = ({ children }) => {
  const [member, setMember] = useState(null); // null checking, false anon, obj authed

  const check = useCallback(async () => {
    const t = getMemberToken();
    if (!t) { setMember(false); return; }
    try {
      const { data } = await axios.get(`${API}/member/me`, { headers: { Authorization: `Bearer ${t}` } });
      setMember(data);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setMember(false);
    }
  }, []);

  useEffect(() => { check(); }, [check]);

  const requestOtp = useCallback((email) => axios.post(`${API}/member/otp/request`, { email }).then((r) => r.data), []);

  const verifyOtp = useCallback(async (email, code) => {
    const { data } = await axios.post(`${API}/member/otp/verify`, { email, code });
    localStorage.setItem(TOKEN_KEY, data.token);
    await check();
    return data;
  }, [check]);

  const googleExchange = useCallback(async (sessionId) => {
    const { data } = await axios.post(`${API}/member/google/session`, { session_id: sessionId });
    localStorage.setItem(TOKEN_KEY, data.token);
    await check();
    return data;
  }, [check]);

  const confirmIdentity = useCallback(async () => {
    const t = getMemberToken();
    await axios.post(`${API}/member/confirm-identity`, {}, { headers: { Authorization: `Bearer ${t}` } });
    await check();
  }, [check]);

  const logout = useCallback(() => { localStorage.removeItem(TOKEN_KEY); setMember(false); }, []);

  const value = useMemo(
    () => ({ member, requestOtp, verifyOtp, googleExchange, confirmIdentity, logout, refresh: check, isMember: !!member }),
    [member, requestOtp, verifyOtp, googleExchange, confirmIdentity, logout, check]
  );

  return <MemberAuthContext.Provider value={value}>{children}</MemberAuthContext.Provider>;
};

export const useMember = () => {
  const ctx = useContext(MemberAuthContext);
  if (!ctx) throw new Error("useMember must be used within MemberAuthProvider");
  return ctx;
};
